import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../services/api";
import { useChat } from "../../contexts/ChatContext";
import { useCategories } from "../../contexts/CategoryContext";
import BirthDetailsPopup from "../../utils/BirthDetailsPopup";
import LifeGPSCard from "./LifeGPSCard";
import LifeRingsSection from "./rings/LifeRingsSection";
import { LifeGPSContext, LifeGPSData } from "./types";

const SCHEMA_VERSION = 4;

const formatUpdated = (iso: string) => {
  const d = new Date(iso);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toDateString() === new Date().toDateString()
    ? `Updated today at ${time}`
    : `Updated ${d.toLocaleDateString([], { month: "short", day: "numeric" })} at ${time}`;
};

const readUser = (): any | null => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const hasBirthDate = (user: any) =>
  Boolean(
    String(
      user?.dob || user?.date_of_birth || user?.dateOfBirth || user?.birthDate || "",
    ).trim(),
  );

const Shell: React.FC<{ children: React.ReactNode; center?: boolean }> = ({
  children,
  center,
}) => (
  <div
    className={`w-full min-h-screen bg-white px-4 py-6 sm:px-6 sm:py-8 md:px-8 ${
      center ? "flex items-center justify-center" : ""
    }`}
  >
    {children}
  </div>
);

// Page: DOB gate → load saved map (or generate the first one) → render card.
const LifeGPS: React.FC = () => {
  const userId = readUser()?._id ?? null;
  const [searchParams] = useSearchParams();
  const subCategoryId = searchParams.get("category");
  const navigate = useNavigate();
  const { startNewChat, setCurrentChat } = useChat();
  const { categories } = useCategories();

  const [tab, setTab] = useState<"areas" | "direction">("areas");
  const [hasDOB, setHasDOB] = useState(() => hasBirthDate(readUser()));
  const [showBirthPopup, setShowBirthPopup] = useState(
    () => !hasBirthDate(readUser()),
  );
  const [data, setData] = useState<LifeGPSData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True when new timing or new saved context exists since the last map.
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const generate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.post("/backend/lifeGps/create", {
        userId,
        subCategoryId,
      });
      if (!res.data?.success || !res.data?.data) {
        throw new Error(res.data?.message || "Failed to build your Life GPS");
      }
      setData(res.data.data as LifeGPSData);
      setUpdateAvailable(Boolean(res.data.updateAvailable));
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to build your Life GPS",
      );
    } finally {
      setIsLoading(false);
    }
  }, [userId, subCategoryId]);

  // Show the saved map instantly; only call the AI when there isn't one yet.
  useEffect(() => {
    if (tab !== "direction" || !hasDOB || !userId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.get(`/backend/lifeGps/latest/${userId}`);
        if (cancelled) return;
        if (res.data?.data?.schemaVersion === SCHEMA_VERSION) {
          setData(res.data.data as LifeGPSData);
          setUpdateAvailable(Boolean(res.data.updateAvailable));
          setIsLoading(false);
          return;
        }
      } catch {
        // Fall through to generating a fresh map.
      }
      if (!cancelled) await generate();
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, hasDOB, userId, generate]);

  // "Ask Astria": open a talk chat with the composer pre-filled with this
  // Life GPS so the user only has to finish the question.
  const handleAsk = async (ctx: LifeGPSContext, question?: string) => {
    const talkCategory =
      categories.find(
        (c) => c.name === "HealJai Talk" || c.name === "Astria Talk",
      ) || categories.find((c) => c.name === "Companion Talk");
    if (!talkCategory) {
      toast.error("Chat is not available right now");
      return;
    }
    try {
      const newChat = await startNewChat(talkCategory.id);
      setCurrentChat(newChat);
      const s = ctx.states;
      const prefill =
        `My Life GPS says: "${ctx.oneSentence}" ` +
        `(Direction: ${s.direction}, Pace: ${s.pace}, Energy: ${s.energy}, Focus: ${s.focus}). ` +
        `My question: ${question ?? ""}`;
      navigate(`/sanctuary?chat=${newChat.id}`, {
        state: { lifeGpsPrefill: prefill },
      });
    } catch {
      toast.error("Could not open the chat. Please try again.");
    }
  };

  const tabs = (
    <div
      role="tablist"
      aria-label="Life GPS view"
      className="mx-auto mb-5 flex w-fit rounded-full bg-slate-100 p-1"
    >
      {(
        [
          ["areas", "Life Areas"],
          ["direction", "Direction"],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={tab === id}
          onClick={() => setTab(id)}
          className={`rounded-full px-5 py-1.5 text-sm font-semibold transition ${
            tab === id
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (tab === "areas") {
    return (
      <Shell>
        {tabs}
        <LifeRingsSection />
      </Shell>
    );
  }

  if (showBirthPopup && !data) {
    return (
      <Shell>
        {tabs}
        <BirthDetailsPopup
          isOpen={showBirthPopup}
          category="LifeGraph"
          onClose={() => setShowBirthPopup(false)}
          onSave={() => {
            setShowBirthPopup(false);
            setHasDOB(true);
          }}
        />
      </Shell>
    );
  }

  if (!hasDOB) {
    return (
      <Shell>
        {tabs}
        <div className="mx-auto max-w-2xl rounded-3xl border border-gray-200 bg-gray-50 p-8 text-center shadow-sm">
          <h2 className="mb-3 text-2xl font-semibold text-gray-900">
            Enter your date of birth first
          </h2>
          <p className="mb-6 text-gray-600">
            We need your birth details before we can build your Life GPS.
          </p>
          <button
            type="button"
            className="rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
            onClick={() => setShowBirthPopup(true)}
          >
            Enter DOB now
          </button>
        </div>
      </Shell>
    );
  }

  if (isLoading && !data) {
    return (
      <Shell>
        {tabs}
        <div className="mt-16 flex flex-col items-center text-center gap-4">
          <div className="relative h-16 w-16">
            <div className="absolute inset-0 rounded-full border-4 border-slate-200" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-indigo-500 border-r-sky-500 border-b-emerald-500 animate-spin" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">
              Charting your Life GPS
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Reading your direction, pace, energy and focus...
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (error && !data) {
    return (
      <Shell>
        {tabs}
        <div className="mx-auto max-w-2xl rounded-3xl border border-red-200 bg-white p-6 sm:p-8 text-center">
          <p className="text-red-500">{error}</p>
          <button
            type="button"
            className="mt-4 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
            onClick={generate}
          >
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  if (!data) return null;

  return (
    <Shell>
        {tabs}
      <div className="mx-auto max-w-3xl">
        <LifeGPSCard data={data} updating={isLoading} onAsk={handleAsk} />
        <div className="mt-4 pb-6 text-center">
          <button
            type="button"
            disabled={isLoading || !updateAvailable}
            onClick={generate}
            className="rounded-full border border-slate-200 bg-white px-5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {isLoading ? "Updating..." : "Update my Life GPS"}
          </button>
          <p className="mt-2 text-xs text-slate-500">
            {formatUpdated(data.generatedAt)}
            {!updateAvailable && !isLoading
              ? " · Your Life GPS is already up to date."
              : ""}
          </p>
          {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
        </div>
      </div>
    </Shell>
  );
};

export default LifeGPS;
